import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { apiRequest, thinkApiRequest } from "../Auth/AuthConfig.tsx";
import { IdTokenClaims, InteractionRequiredAuthError } from "@azure/msal-browser";

interface CustomIdTokenClaims extends IdTokenClaims {
  oid?: string;
}

const useAccessToken = () => {
  const { accounts, instance } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [thinkAccessToken, setThinkAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [error, setError] = useState<Error | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!instance) {
      console.error("MSAL instance is not initialized yet.");
      return;
    }

    instance.initialize().then(() => {
      setIsInitialized(true);
    }).catch((err) => {
      console.error("Error initializing MSAL instance:", err);
      setError(err);
    });
  }, [instance]);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (isInitialized && accounts.length > 0 && instance) {
        const request = {
          ...apiRequest,
          account: accounts[0],
        };

        try {
          const response = await instance.acquireTokenSilent(request);
          localStorage.setItem("lbmc_token", response.accessToken);
          setAccessToken(response.accessToken);

          const claims = response.idTokenClaims as CustomIdTokenClaims;
          setUserId(claims.oid || "");

          if (response.expiresOn) {
            const timeToExpire = response.expiresOn.getTime() - Date.now();
            setTimeout(fetchAccessToken, Math.max(timeToExpire - 120000, 10000));
          }
        } catch (err: any) {
          if (err instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(request);
            // Uncomment the following lines if you want to handle interactive token acquisition
            // const interactiveResponse = await instance.acquireTokenPopup(request);
            // setAccessToken(interactiveResponse.accessToken);
            // localStorage.setItem("lbmc_token", interactiveResponse.accessToken);
          } else {
            console.error("Error fetching access token:", err);
            setError(err);
          }
        }
      }
    };

    const fetchThinkAccessToken = async () => {
      if (isInitialized && accounts.length > 0 && instance) {
        const request = {
          ...thinkApiRequest,
          account: accounts[0],
        };

        try {
          const response = await instance.acquireTokenSilent(request);
          setThinkAccessToken(response.accessToken);


          if (response.expiresOn) {
            const timeToExpire = response.expiresOn.getTime() - Date.now();
            setTimeout(fetchThinkAccessToken, Math.max(timeToExpire - 120000, 10000));
          }
        } catch (err: any) {
          if (err instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(request);
            // Uncomment the following lines if you want to handle interactive token acquisition
            // const interactiveResponse = await instance.acquireTokenPopup(request);
            // setAccessToken(interactiveResponse.accessToken);
            // localStorage.setItem("lbmc_token", interactiveResponse.accessToken);
          } else {
            console.error("Error fetching access token:", err);
            setError(err);
          }
        }
      }
    };

    if (isInitialized) {
      fetchAccessToken();
      fetchThinkAccessToken();
    }
  }, [accounts, instance, isInitialized]);

  return { accessToken, thinkAccessToken, userId, error };
};

export default useAccessToken;
